import type { AxiosRequestConfig } from "axios";

import { streamFetch } from "api/stream-fetch";

/** Event shape from SSE stream (stage + content). */
export interface ClientStreamEvent {
  stage?: string;
  content?: string;
}

export interface StreamingMutatorOptions {
  signal?: AbortSignal;
  /** When provided, every chunk is forwarded here and the promise resolves with void when the stream ends. */
  onChunk?: (chunk: ClientStreamEvent) => void;
}

/** Config that may include AbortSignal (axios types vary by version). */
type ConfigWithSignal = AxiosRequestConfig & { signal?: AbortSignal };

/**
 * Generic Orval mutator for SSE streaming endpoints.
 * When onChunk is provided: forwards every event to onChunk and resolves void when the stream ends.
 * When onChunk is not provided: resolves with the payload from the event where stage === "completed".
 */
export const streamingMutator = <T = unknown>(
  config: AxiosRequestConfig,
  options?: StreamingMutatorOptions,
): Promise<T | void> => {
  let completedPayload: T | null = null;
  const signal = options?.signal ?? (config as ConfigWithSignal).signal;
  const hasOnChunk = Boolean(options?.onChunk);

  return streamFetch(config.url ?? "", {
    body: config.data,
    method: (config.method?.toUpperCase() as "POST" | "GET") || "POST",
    signal,
    parseJson: true,
    onChunk(parsed: unknown) {
      const event = parsed as ClientStreamEvent;

      options?.onChunk?.(event);

      if (!hasOnChunk && event?.stage === "completed" && event.content) {
        try {
          const payload = JSON.parse(event.content) as T;
          completedPayload = payload;
        } catch (parseErr) {
          console.warn("[streaming-mutator] Failed to parse completed event content:", parseErr);
        }
      }
    },
    onError(error) {
      console.warn("[streaming-mutator] onError:", error);
    },
  }).then((result) => {
    if (result.aborted) {
      const err = new Error("Stream aborted");
      err.name = "AbortError";
      throw err;
    }
    if (hasOnChunk) {
      return undefined;
    }
    if (completedPayload !== null) {
      return completedPayload;
    }
    console.error(
      "[streaming-mutator] Stream ended without completed event. Chunks received:",
      result.chunks?.length ?? 0,
      "last chunk:",
      result.chunks?.[result.chunks.length - 1],
    );
    throw new Error("Stream ended without completed event");
  });
};
